<script lang="ts" setup>
import { AppAutocomplete } from '@/core/components';
import { Loader } from '@googlemaps/js-api-loader';
import _ from 'lodash';

const place = defineModel<string | number | object>({ default: { description: '' } });

const props = defineProps({
  country: { type: String, default: '' },
  label: { type: String, default: 'Enter Location' },
  errorMessages: { type: String, default: '' },
});

const config = useRuntimeConfig();
const searchService = ref();
const placeService = ref();
const sessionToken = ref();

const query = ref('');
const places = ref([]);
const loading = ref(false);

const emits = defineEmits<{
  (e: 'update:modelValue', value: [String, Object]): void;
}>();

const loader = new Loader({
  apiKey: config.public.googleApiKey,
  version: 'weekly',
  libraries: ['places'],
  ...(props.country !== '' && { region: props.country.toUpperCase() }),
});

onMounted(async () => {
  const { AutocompleteService, AutocompleteSessionToken, PlacesService } = await loader.importLibrary('places');

  searchService.value = new AutocompleteService();
  placeService.value = new PlacesService(document.createElement('div'));
  sessionToken.value = new AutocompleteSessionToken();

  // set selected place from modelValue
  if (place.value) {
    const { place_id } = place.value;
    if (!place_id) return;

    updateSelected(place_id);
  }
});

watch(
  query,
  _.debounce(async () => {
    const value = query.value;

    if (value === '') {
      places.value = [];
      return;
    }

    loading.value = true;

    // search query using google places api
    const { predictions } = await searchService.value.getPlacePredictions({
      input: value,
      sessionToken: sessionToken.value,
      types: ['address'],
      ...(props.country !== '' && { componentRestrictions: { country: props.country } }),
    });
    loading.value = false;

    if (!predictions) return;

    places.value = predictions;
  }, 500)
);

function updateSelected(placeId) {
  if (!placeId) return;

  placeService.value.getDetails(
    {
      placeId,
      sessionToken: sessionToken.value,
      fields: ['geometry', 'formatted_address'],
    },
    (data, status) => {
      if (status !== 'OK') return;

      // clone place and add place_id
      place.value = { ...data, place_id: placeId };
    }
  );
}

const selectedItem = computed(() => {
  const { place_id, formatted_address } = place.value;
  if (!place_id) return null;

  return { place_id, description: formatted_address };
});
</script>

<template>
  <AppAutocomplete
    :model-value="selectedItem"
    item-title="description"
    :label="label"
    hide-no-data
    return-object
    :items="places"
    :loading="loading"
    :error-messages="errorMessages"
    data-testid="google-place-input"
    @update:search="query = $event"
    @update:model-value="updateSelected($event?.place_id)"
    v-bind="$attrs"
  />
</template>
